import { useMemo, useState } from 'react';
import { type ChainflipAsset } from '@chainflip/utils/chainflip';
import { toEndOfUtcDayString, toStartOfUtcDay, toStartOfUtcDayString } from '@chainflip/utils/date';
import { isNotNullish } from '@chainflip/utils/guard';
import { differenceInDays, subMonths } from 'date-fns';
import { Link } from '@/shared/components/atoms/Link';
import { SkeletonLine } from '@/shared/components/atoms/LoadingSkeleton';
import Button from '@/shared/components/flip-ui-kit/Button';
import { ApyPeriodSelect } from '@/shared/components/lending/ApyPeriodSelect';
import BorrowRewardsTooltip from '@/shared/components/lending/BorrowRewardsTooltip';
import { IncentivisedApyText } from '@/shared/components/lending/IncentivisedApyText';
import { interestRateModelChartProps } from '@/shared/components/lending/InterestRateModelChart';
import {
  CHART_STAT_COLOR,
  marketApyChartProps,
} from '@/shared/components/lending/MarketSupplyChart';
import { StatsWithChart } from '@/shared/components/lending/StatsWithChart';
import { TokenWithChainByAsset } from '@/shared/components/logo/TokenWithChain';
import QuestionMarkTooltip from '@/shared/components/QuestionMarkTooltip';
import { TimeframeTabSelect } from '@/shared/components/TimeframeTabSelect';
import { isLiquidityProvisionApp } from '@/shared/featureFlags';
import { getActiveBoostLoans } from '@/shared/graphql/cache/lending';
import {
  getLendingBorrowApyStatsQuery,
  getLendingSupplyApyStatsQuery,
} from '@/shared/graphql/lp/lending';
import { useGqlQuery } from '@/shared/hooks/useGqlQuery';
import { useIncentives } from '@/shared/hooks/useIncentives';
import { useLendingConfig } from '@/shared/hooks/useLendingConfig';
import { useLendingPools } from '@/shared/hooks/useLendingPools';
import {
  oneMonth,
  oneWeek,
  useCustomTimeFrameSelection,
  yearToDate,
} from '@/shared/hooks/useTimeframeSelection';
import WindowIcon from '@/shared/icons/flip-ui-kit/large/Window';
import ArrowBackIcon from '@/shared/icons/flip-ui-kit/small/ArrowBack';
import { apyToText } from '@/shared/utils/boost';
import { chainflipAssetMap } from '@/shared/utils/env';
import {
  type ApyAveragePeriodOption,
  type BorrowApyPeriodOption,
  BORROW_APY_PERIODS,
  DEFAULT_APY_PERIOD,
  EMPTY_APY_BY_PERIOD,
  SUPPLY_APY_PERIODS,
} from '@/shared/utils/lending';
import {
  formatUsdValue,
  formatWithCommas,
  pipsToPercent,
  pipsToPercentString,
} from '@/shared/utils/numbers';
import TokenAmount from '@/shared/utils/TokenAmount';
import { useOraclePrices } from '../../hooks/useOraclePrice';
import { BoostIcon } from '../logo/BoostPoolLogo';
import { Tooltip } from '../molecules/Tooltip';

const StalePill = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M8 0.5C12.1421 0.5 15.5 3.85786 15.5 8C15.5 12.1421 12.1421 15.5 8 15.5C3.85786 15.5 0.5 12.1421 0.5 8C0.5 3.85786 3.85786 0.5 8 0.5Z"
      fill="#463719"
    />
    <path
      d="M8 0.5C12.1421 0.5 15.5 3.85786 15.5 8C15.5 12.1421 12.1421 15.5 8 15.5C3.85786 15.5 0.5 12.1421 0.5 8C0.5 3.85786 3.85786 0.5 8 0.5Z"
      stroke="#684C17"
    />
    <circle cx="8" cy="8" r="3" fill="#EC9F0A" />
  </svg>
);

const FreshPill = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M8 0.5C12.1421 0.5 15.5 3.85786 15.5 8C15.5 12.1421 12.1421 15.5 8 15.5C3.85786 15.5 0.5 12.1421 0.5 8C0.5 3.85786 3.85786 0.5 8 0.5Z"
      fill="#0E2C1D"
    />
    <path
      d="M8 0.5C12.1421 0.5 15.5 3.85786 15.5 8C15.5 12.1421 12.1421 15.5 8 15.5C3.85786 15.5 0.5 12.1421 0.5 8C0.5 3.85786 3.85786 0.5 8 0.5Z"
      stroke="#2C6248"
    />
    <circle cx="8" cy="8" r="3" fill="#46DA93" />
  </svg>
);

const OracleToolTipContent = ({
  oraclePrice,
}: {
  oraclePrice: NonNullable<ReturnType<typeof useOraclePrices>>[keyof NonNullable<
    ReturnType<typeof useOraclePrices>
  >];
}) => (
  <div className="flex flex-col gap-y-2 p-2">
    <div className="flex items-center gap-x-2 border-b border-cf-gray-5 pb-2">
      {oraclePrice.isStale ? <StalePill /> : <FreshPill />}
      <span className="font-aeonikMedium text-14 text-cf-white">
        {oraclePrice.isStale ? 'Price update delayed' : 'Price is up to date'}
      </span>
    </div>
    <div className="flex flex-col gap-y-1 border-b border-cf-gray-5 pb-2 text-12 text-cf-light-3">
      <span>
        {oraclePrice.isStale
          ? 'The price is updating with a delay. Until this is resolved, borrowing or supplying may be unavailable for this market.'
          : 'Borrowing and supplying is available for this market.'}
      </span>
      <Link
        href="https://docs.chainflip.io/lending/collateralisation#oracle-safety-controls"
        color="green"
        target="_blank"
      >
        Learn more
      </Link>
    </div>
    <div className="text-12 text-cf-light-2">
      Last updated at: {oraclePrice.lastUpdated.toLocaleString()}
    </div>
  </div>
);
const OraclePrice = ({
  oraclePrice,
}: {
  oraclePrice:
    | NonNullable<ReturnType<typeof useOraclePrices>>[keyof NonNullable<
        ReturnType<typeof useOraclePrices>
      >]
    | undefined;
}) => (
  <div className="flex items-center gap-x-1 font-aeonikMedium">
    <QuestionMarkTooltip content="Trusted Chainlink oracle price, as witnessed by Chainflip validators. Used to value the account LTV and liquidation thresholds." />
    <span className="text-cf-light-2">Oracle Price:</span>
    {isNotNullish(oraclePrice) ? (
      <div className="flex items-center gap-x-1.5">
        <span className="text-cf-white">${formatWithCommas(oraclePrice.usdPrice)}</span>
        <Tooltip content={<OracleToolTipContent oraclePrice={oraclePrice} />}>
          {oraclePrice.isStale ? <StalePill /> : <FreshPill />}
        </Tooltip>
      </div>
    ) : (
      <SkeletonLine width={100} height={16} />
    )}
  </div>
);

const options = [
  oneWeek(),
  oneMonth(),
  {
    label: '6M',
    longLabel: 'Past 6 months',
    value: differenceInDays(new Date(), subMonths(new Date(), 6)),
  } as const,
  yearToDate(),
];

const useMarketTimeframeSelection = () =>
  useCustomTimeFrameSelection({ timeframeTabOptions: options, initialTimeframe: '6M' });

export const MarketIdContent = ({
  asset,
  ctaContent,
  prevHref,
}: {
  asset: ChainflipAsset;
  ctaContent: React.JSX.Element;
  prevHref: string;
}) => {
  const token = chainflipAssetMap[asset];

  const prices = useOraclePrices();
  const price = prices?.[asset];

  const {
    targetLtvThresholdBps,
    softLiquidationLtvThresholdBps,
    isLoading: isConfigLoading,
  } = useLendingConfig();

  const { lendingPools, isLoading: lendingPoolsDataLoading } = useLendingPools({ asset });
  const lendingPool = lendingPools.at(0);
  const {
    totalAmountUsd,
    totalAmount,
    projectedSupplyApyByPeriod,
    projectedBorrowApyByPeriod,
    totalBorrowedAmountUsd,
    totalBorrowedAmount,
    interestRateBps,
    liquidationFeeBps,
    utilisationCapBps,
  } = lendingPool ?? {
    totalAmountUsd: '0',
    totalAmount: '0',
    projectedSupplyApyByPeriod: EMPTY_APY_BY_PERIOD,
    projectedBorrowApyByPeriod: { CURRENT: 0, ...EMPTY_APY_BY_PERIOD },
    totalBorrowedAmountUsd: '0',
    totalBorrowedAmount: '0',
    interestRateBps: 0,
    liquidationFeeBps: 0,
    utilisationCapBps: 10_000,
  };
  const interestRatePercent = pipsToPercent(interestRateBps);

  const [supplyApyPeriod, setSupplyApyPeriod] =
    useState<ApyAveragePeriodOption>(DEFAULT_APY_PERIOD);
  const [borrowApyPeriod, setBorrowApyPeriod] = useState<BorrowApyPeriodOption>(DEFAULT_APY_PERIOD);

  const borrowApy =
    borrowApyPeriod.value === 'CURRENT'
      ? interestRatePercent
      : projectedBorrowApyByPeriod[borrowApyPeriod.value];

  const borrowApyField =
    borrowApyPeriod.value === 'CURRENT'
      ? ('avgInterestRateBps' as const)
      : (`projectedBorrowApy${borrowApyPeriod.value}` as const);

  const borrowLegendLabel = `Borrow Rate, ${
    borrowApyPeriod.value === 'CURRENT' ? borrowApyPeriod.label : borrowApyPeriod.description
  }`;

  const latestSupplyApy = projectedSupplyApyByPeriod[supplyApyPeriod.value];

  const {
    selectedTimeframe: supplySelectedTimeframe,
    setSelectedTimeframe: setSupplySelectedTimeframe,
    chartStartDate: supplyChartStartDate,
    chartEndDate: supplyChartEndDate,
    timeframeTabOptions: supplyTimeframeTabOptions,
  } = useMarketTimeframeSelection();
  const {
    selectedTimeframe: borrowSelectedTimeframe,
    setSelectedTimeframe: setBorrowSelectedTimeframe,
    chartStartDate: borrowChartStartDate,
    chartEndDate: borrowChartEndDate,
    timeframeTabOptions: borrowTimeframeTabOptions,
  } = useMarketTimeframeSelection();

  const { data: borrowApyData, isLoading: isBorrowApyDataLoading } = useGqlQuery(
    getLendingBorrowApyStatsQuery,
    {
      context: { clientName: 'lpProcessor' },
      variables: {
        asset,
        minTimestamp: toStartOfUtcDayString(borrowChartStartDate),
        maxTimestamp: toEndOfUtcDayString(borrowChartEndDate),
      },
      refetchInterval: 30_000,
    },
  );

  const { data: supplyApyData, isLoading: isSupplyApyDataLoading } = useGqlQuery(
    getLendingSupplyApyStatsQuery,
    {
      context: { clientName: 'lpProcessor' },
      variables: {
        asset,
        minTimestamp: toStartOfUtcDayString(supplyChartStartDate),
        maxTimestamp: toEndOfUtcDayString(supplyChartEndDate),
      },
      refetchInterval: 30_000,
    },
  );

  const { borrowApyChartData, narrowedBorrowChartStartDate } = useMemo(() => {
    const currentDay = toStartOfUtcDayString(borrowChartEndDate);
    const earliestDate = toStartOfUtcDay(
      borrowApyData?.lendingPoolStats?.groupedAggregates?.reduce(
        (acc, curr) => (!curr.keys?.[1] || acc < curr.keys?.[1] ? acc : curr.keys[1]),
        currentDay,
      ) ?? currentDay,
    );

    const chartData =
      borrowApyData?.lendingPoolStats?.groupedAggregates
        ?.map(({ keys, average }) => {
          const timestamp = keys?.[1];
          if (!timestamp) return undefined;

          let value: number;
          if (new Date(currentDay).getTime() === new Date(timestamp).getTime()) {
            value = borrowApy;
          } else if (borrowApyField === 'avgInterestRateBps') {
            // Despite the name the processor stores the raw protocol rate, where 1e6 is 100%.
            value = pipsToPercent(Number(average?.avgInterestRateBps ?? 0) / 100);
          } else {
            value = Number(average?.[borrowApyField] ?? 0);
          }

          return { day: new Date(timestamp).getTime(), value };
        })
        .filter(isNotNullish) ?? [];

    return { borrowApyChartData: chartData, narrowedBorrowChartStartDate: earliestDate };
  }, [borrowApyData, borrowApy, borrowApyField, borrowChartEndDate]);

  const { supplyApyChartData, narrowedSupplyChartStartDate } = useMemo(() => {
    const supplyApyField = `projectedApy${supplyApyPeriod.value}` as const;
    const currentDay = toStartOfUtcDayString(supplyChartEndDate);
    const earliestDate = toStartOfUtcDay(
      supplyApyData?.lendingPoolStats?.groupedAggregates?.reduce(
        (acc, curr) => (!curr.keys?.[1] || acc < curr.keys?.[1] ? acc : curr.keys[1]),
        currentDay,
      ) ?? currentDay,
    );

    const chartData =
      supplyApyData?.lendingPoolStats?.groupedAggregates
        ?.map(({ keys, average }) => {
          const timestamp = keys?.[1];
          return timestamp
            ? {
                day: new Date(timestamp).getTime(),
                value:
                  new Date(currentDay).getTime() === new Date(timestamp).getTime()
                    ? latestSupplyApy
                    : Number(average?.[supplyApyField] ?? 0),
              }
            : undefined;
        })
        .filter(isNotNullish) ?? [];

    return { supplyApyChartData: chartData, narrowedSupplyChartStartDate: earliestDate };
  }, [supplyApyData, latestSupplyApy, supplyApyPeriod.value, supplyChartEndDate]);

  const { isLoading: isIncentivesLoading, activeSeason } = useIncentives();

  const borrowIncentive = activeSeason?.pools.find(
    (pool) => pool.asset === asset && pool.type === 'BORROW',
  );

  const borrowRewardsApy = borrowIncentive?.apy ?? 0;
  const borrowDailyPoints = borrowIncentive?.dailyPoints ?? 0;

  const { data: boostLoans, isLoading: isBoostLoansLoading } = useGqlQuery(getActiveBoostLoans, {
    context: { clientName: 'statechainCache' },
    enabled: asset === 'Btc',
    refetchInterval: 12_000,
  });

  const boostLoansAmount = boostLoans?.loans?.aggregates?.sum?.principalAmount ?? 0n;
  const btcPrice = prices?.Btc.usdPrice ?? 0;
  const boostLoansAmountUsd =
    asset === 'Btc' ? TokenAmount.fromAsset(boostLoansAmount, 'Btc').toUsdNumber(btcPrice) : 0;

  const supplyStatsData = [
    {
      title: 'Supplied',
      tooltip: 'The amount currently supplied to this market.',
      value: `${formatUsdValue(totalAmountUsd, false)}`,
      subValue: `${TokenAmount.fromAsset(totalAmount, asset)?.toFormatted() ?? '0'} ${token.symbol}`,
      isLoading: lendingPoolsDataLoading,
      timeframe: 'Current',
    },
    {
      title: 'Supply APY',
      tooltip: `Annual Percentage Yield (APY) estimates the annual return in a lending pool. We take hourly snapshots of fees earned in the pool, average them over the past ${supplyApyPeriod.description}, and project the result over a year.`,
      value: apyToText(projectedSupplyApyByPeriod[supplyApyPeriod.value]),
      isLoading: lendingPoolsDataLoading || isIncentivesLoading,
      timeframe: (
        <ApyPeriodSelect
          periods={SUPPLY_APY_PERIODS}
          selected={supplyApyPeriod}
          onChange={setSupplyApyPeriod}
        />
      ),
    },
  ];
  const borrowStatsData = [
    {
      title: 'Borrowed',
      tooltip: 'The amount currently lent out to borrowers in this market.',
      value: `${formatUsdValue(totalBorrowedAmountUsd, false)}`,
      subValue: `${TokenAmount.fromAsset(totalBorrowedAmount, asset)?.toFormatted() ?? '0'} ${token.symbol}`,
      isLoading: lendingPoolsDataLoading || isBoostLoansLoading,
      caption:
        boostLoansAmountUsd > 0 ? (
          <span className="flex items-center gap-x-1">
            <BoostIcon width={16} height={16} />
            {formatUsdValue(boostLoansAmountUsd, false)} via Boost
          </span>
        ) : undefined,
      timeframe: 'Current',
    },
    {
      title: 'Borrow Rate',
      tooltip:
        borrowApyPeriod.value === 'CURRENT'
          ? 'The current interest rate charged to new borrows in this market.'
          : `The average annualized borrowing rate across the market over the past ${borrowApyPeriod.description}.`,
      value: (
        <IncentivisedApyText
          tooltipContent={
            <BorrowRewardsTooltip
              rewardsApy={borrowRewardsApy}
              baseApy={borrowApy}
              seasonIndex={activeSeason?.seasonIndex ?? 0}
              dailyPoints={borrowDailyPoints}
            />
          }
          baseApy={borrowApy}
          rewardsApy={borrowRewardsApy}
          totalApy={borrowApy - borrowRewardsApy}
          isLoading={isIncentivesLoading || lendingPoolsDataLoading}
          showNegativeRewards
          showBreakdown
        />
      ),
      isLoading: lendingPoolsDataLoading || isIncentivesLoading,
      timeframe: (
        <ApyPeriodSelect
          periods={BORROW_APY_PERIODS}
          selected={borrowApyPeriod}
          onChange={setBorrowApyPeriod}
        />
      ),
    },
    {
      title: 'Max LTV',
      value: `${pipsToPercentString(targetLtvThresholdBps ?? 0)}`,
      isLoading: isConfigLoading,
      timeframe: 'Current',
    },
    {
      title: 'Liquidation LTV',
      tooltip:
        'Liquidations start at this LTV threshold. Crossing it may cause the loan accounts collateral to be sold.',
      value: `${pipsToPercentString(softLiquidationLtvThresholdBps ?? 0)}`,
      isLoading: isConfigLoading,
      timeframe: 'Current',
    },
    {
      title: 'Liquidation Fee',
      tooltip: 'A fee charged on the collateral sold during liquidation.',
      value: `${pipsToPercentString(liquidationFeeBps)}`,
      isLoading: lendingPoolsDataLoading,
      timeframe: 'Current',
    },
    {
      title: 'Utilisation Cap',
      tooltip: 'Utilization cap ensures all positions can be liquidated at current oracle prices.',
      value: `${pipsToPercentString(utilisationCapBps)}`,
      isLoading: lendingPoolsDataLoading,
      timeframe: 'Current',
    },
  ];

  const { interestRateModelData, interestRateModelStatsData } = useMemo(() => {
    let rawData;
    if (lendingPool) {
      rawData = {
        junctionUtilisationBps: lendingPool.junctionUtilisationBps,
        interestAtJunctionBps: lendingPool.interestAtJunctionBps,
        interestAtZeroUtilisationBps: lendingPool.interestAtZeroUtilisationBps,
        interestAtMaxUtilisationBps: lendingPool.interestAtMaxUtilisationBps,
        utilisationRateBps: lendingPool.utilisationRateBps,
      };
    }

    const interestRateModelStats = [
      {
        title: 'Borrow Rate',
        tooltip: 'The current interest rate charged to new borrows in this market.',
        value: pipsToPercentString(lendingPool?.interestRateBps ?? 0),
        isLoading: lendingPoolsDataLoading,
        timeframe: 'Current',
      },
      {
        title: 'Utilisation Rate',
        tooltip: 'The percentage of supplied assets that is currently borrowed.',
        value: pipsToPercentString(lendingPool?.utilisationRateBps ?? 0),
        isLoading: lendingPoolsDataLoading,
        timeframe: 'Current',
      },
    ];

    return { interestRateModelData: rawData, interestRateModelStatsData: interestRateModelStats };
  }, [lendingPool, lendingPoolsDataLoading]);

  return (
    <div className="flex flex-col gap-y-6">
      {isLiquidityProvisionApp && (
        <Link className="flex w-fit items-center gap-x-2 font-aeonikMedium text-14" href={prevHref}>
          <ArrowBackIcon direction="left" />
          Back
        </Link>
      )}
      <div className="flex flex-wrap items-center justify-between gap-6">
        <div className="flex flex-col gap-y-2">
          <div className="flex items-center gap-x-2">
            <TokenWithChainByAsset asset={asset} size="large" />
            <div className="flex items-baseline gap-x-2">
              <span className="font-aeonikMedium text-24 text-cf-white">{token.symbol} market</span>
              <span className="font-aeonikMedium text-16 text-cf-light-2">
                on {token.chain.name}
              </span>
            </div>
          </div>
          <OraclePrice oraclePrice={price} />
        </div>
        {ctaContent}
      </div>
      <StatsWithChart
        {...marketApyChartProps}
        title="Supply Stats"
        legendLabels={[
          { label: `Supply APY, ${supplyApyPeriod.description}`, color: CHART_STAT_COLOR },
        ]}
        aggregateOptions={{
          startDate: narrowedSupplyChartStartDate,
          endDate: supplyChartEndDate,
        }}
        rawData={supplyApyChartData}
        stats={supplyStatsData}
        rightContent={
          <TimeframeTabSelect
            selected={supplySelectedTimeframe}
            onChange={setSupplySelectedTimeframe}
            timeframeTabs={supplyTimeframeTabOptions}
            pillWidth={36}
          />
        }
        isLoading={isSupplyApyDataLoading || lendingPoolsDataLoading}
      />
      <StatsWithChart
        {...marketApyChartProps}
        title="Borrow Stats"
        legendLabels={[{ label: borrowLegendLabel, color: CHART_STAT_COLOR }]}
        aggregateOptions={{
          startDate: narrowedBorrowChartStartDate,
          endDate: borrowChartEndDate,
        }}
        rawData={borrowApyChartData ?? []}
        stats={borrowStatsData}
        rightContent={
          <TimeframeTabSelect
            selected={borrowSelectedTimeframe}
            onChange={setBorrowSelectedTimeframe}
            timeframeTabs={borrowTimeframeTabOptions}
            pillWidth={36}
          />
        }
        isLoading={isBorrowApyDataLoading || isConfigLoading}
      />
      <StatsWithChart
        {...interestRateModelChartProps}
        title="Interest Rate Model"
        aggregateOptions={{}}
        rawData={interestRateModelData}
        stats={interestRateModelStatsData}
        rightContent={
          <Button
            icon={<WindowIcon className="ml-1" />}
            iconPos="right"
            type="secondary-standard"
            onClick={() =>
              window.open(
                'https://docs.chainflip.io/lending/utilisation#how-the-utilisation-curve-works',
                '_blank',
              )
            }
          >
            Learn More
          </Button>
        }
        isLoading={lendingPoolsDataLoading}
      />
    </div>
  );
};
